// ============================================================================
//  Fase 4 — Validación de formularios del plan de acción
// ----------------------------------------------------------------------------
//  Reemplaza los esquemas Zod del prototipo (src/schemas/index.ts) sin agregar
//  Zod al bundle. Mismas reglas, escritas a mano.
//
//  ESPEJO DEL BACKEND, A PROPÓSITO. Cada regla de aquí existe también en
//  PKG_CRM_CX_PLAN (las llamadas a `exige(...)` y los CHECK de las tablas). Se
//  duplica para que el usuario vea el error al escribir, en vez de después de
//  un viaje al servidor. La autoridad sigue siendo la base: si estas reglas se
//  relajan por error, el backend rechaza igual.
//
//  Los nombres de campo son los del contrato ORDS (docs/API_FASE4.md), no los
//  del prototipo: aquí los identificadores son numéricos y van en camelCase.
//
//  Toda función devuelve:
//    { ok: boolean, errores: { campo: mensaje }, faltan: [mensaje] }
// ============================================================================

const NIVELES_EFECTIVIDAD = ["efectiva", "parcial", "no_efectiva"];
const TIPOS_SOLICITANTE = ["Externo", "Interno"];

const vacio = (v) => v === null || v === undefined || String(v).trim() === "";
const largo = (v) => String(v === null || v === undefined ? "" : v).trim().length;

// Acumulador: recoge todos los errores en lugar de detenerse en el primero.
function crear() {
  const errores = {};
  const faltan = [];
  return {
    exige(campo, valor, mensaje, min) {
      if (vacio(valor) || (min && largo(valor) < min)) {
        if (!errores[campo]) { errores[campo] = mensaje; faltan.push(mensaje); }
      }
    },
    enumera(campo, valor, permitidos, mensaje) {
      if (!vacio(valor) && permitidos.indexOf(valor) === -1) {
        if (!errores[campo]) { errores[campo] = mensaje; faltan.push(mensaje); }
      }
    },
    rango(campo, valor, min, max, mensaje) {
      const n = Number(valor);
      if (vacio(valor) || isNaN(n) || n < min || n > max) {
        if (!errores[campo]) { errores[campo] = mensaje; faltan.push(mensaje); }
      }
    },
    resultado() {
      return { ok: faltan.length === 0, errores, faltan };
    },
  };
}

// ── Hallazgo / incidencia ───────────────────────────────────────────────────
// RN-01: cliente, descripción, categoría y prioridad son obligatorios para que
// el hallazgo salga de borrador. `descripcion`, `tipoHallazgo` e
// `impactoCliente` son los campos que agrega crm_cx_fase4_alter_claim.sql.
export function validaHallazgo(f) {
  const v = crear();
  v.exige("cliente",     f.cliente,     "Selecciona el cliente");
  v.exige("asunto",      f.asunto,      "El asunto debe tener al menos 5 caracteres", 5);
  v.exige("descripcion", f.descripcion, "Describe el hallazgo con al menos 10 caracteres", 10);
  v.exige("categoria",   f.categoria,   "Selecciona la categoría");
  v.exige("prioridad",   f.prioridad,   "Selecciona la prioridad");
  v.exige("area",        f.area,        "Selecciona el área responsable");
  if (!vacio(f.solicitante)) {
    v.exige("solicitante", f.solicitante, "Indica el nombre del solicitante", 2);
  }
  v.enumera("solicitanteTipo", f.solicitanteTipo, TIPOS_SOLICITANTE,
            "El solicitante debe ser Externo o Interno");
  return v.resultado();
}

// ── Plan de acción (RN-02) ──────────────────────────────────────────────────
export function validaPlan(f) {
  const v = crear();
  v.exige("idClaim",         f.idClaim,         "Falta el hallazgo de origen");
  v.exige("titulo",          f.titulo,          "El título del plan debe tener al menos 5 caracteres", 5);
  // Solo se captura el área. El responsable sale del gerente del área y la
  // prioridad se hereda de la incidencia: pedirlos aquí permitiría que el plan
  // contradijera a su origen.
  v.exige("idArea",          f.idArea,          "Selecciona el área responsable");
  v.exige("fechaCompromiso", f.fechaCompromiso, "La fecha compromiso es obligatoria");
  return v.resultado();
}

// ── Actividad ───────────────────────────────────────────────────────────────
export function validaTarea(f) {
  const v = crear();
  v.exige("titulo",          f.titulo,          "Describe la actividad (mínimo 3 caracteres)", 3);
  v.exige("fechaCompromiso", f.fechaCompromiso, "La fecha compromiso de la actividad es obligatoria");
  return v.resultado();
}

export function validaAvanceTarea(avance) {
  const v = crear();
  v.rango("avance", avance, 0, 100, "El avance debe estar entre 0 y 100");
  return v.resultado();
}

// ── Aceptación / rechazo (RN-04) ────────────────────────────────────────────
export function validaRechazo(f) {
  const v = crear();
  v.exige("motivo", f.motivo, "La justificación del rechazo es obligatoria (mínimo 10 caracteres)", 10);
  return v.resultado();
}

// ── Causa raíz ──────────────────────────────────────────────────────────────
export function validaCausaRaiz(f) {
  const v = crear();
  v.exige("causaRaiz", f.causaRaiz, "La causa raíz debe tener al menos 10 caracteres", 10);
  return v.resultado();
}

// ── Evidencias (RN-15) ──────────────────────────────────────────────────────
export function validaEvidencia(f) {
  const v = crear();
  v.exige("nombre", f.nombre, "La evidencia necesita un nombre (mínimo 3 caracteres)", 3);
  return v.resultado();
}

// Aprobar no pide nada; rechazar exige comentario.
export function validaRevisionEvidencia(f) {
  const v = crear();
  if (!f.aprueba) {
    v.exige("comentario", f.comentario,
            "El rechazo de una evidencia requiere comentario (mínimo 5 caracteres)", 5);
  }
  return v.resultado();
}

export function validaObservaciones(f) {
  const v = crear();
  v.exige("comentario", f.comentario, "Escribe las observaciones (mínimo 5 caracteres)", 5);
  return v.resultado();
}

// ── Cierre (RN-08/09) ───────────────────────────────────────────────────────
// Valida solo el FORMULARIO. Que el plan reúna las condiciones para cerrarse
// (avance, actividades, evidencia aprobada) lo decide puedeCerrar() en
// cx.rules.js, y la base lo vuelve a verificar.
export function validaCierre(f) {
  const v = crear();
  v.exige("resultadoObtenido", f.resultadoObtenido,
          "Registra el resultado obtenido (mínimo 10 caracteres)", 10);
  v.exige("nivelEfectividad", f.nivelEfectividad, "Indica el nivel de efectividad");
  v.enumera("nivelEfectividad", f.nivelEfectividad, NIVELES_EFECTIVIDAD,
            "El nivel de efectividad debe ser efectiva, parcial o no_efectiva");
  if (!vacio(f.calificacion)) {
    v.rango("calificacion", f.calificacion, 1, 5, "La calificación debe estar entre 1 y 5");
  }
  return v.resultado();
}

// ── Reapertura (RN-10) ──────────────────────────────────────────────────────
export function validaReapertura(f) {
  const v = crear();
  v.exige("motivo", f.motivo, "El motivo de reapertura es obligatorio (mínimo 10 caracteres)", 10);
  return v.resultado();
}

// ── Cambio de fecha (RN-06) ─────────────────────────────────────────────────
export function validaCambioFecha(f) {
  const v = crear();
  v.exige("fechaPropuesta", f.fechaPropuesta, "Indica la nueva fecha propuesta");
  v.exige("motivo", f.motivo, "El motivo del cambio es obligatorio (mínimo 10 caracteres)", 10);
  return v.resultado();
}

// ── Comentario ──────────────────────────────────────────────────────────────
export function validaComentario(f) {
  const v = crear();
  v.exige("cuerpo", f.cuerpo, "El comentario está vacío", 1);
  return v.resultado();
}
